import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, Download, CreditCard, Banknote, QrCode, ArrowLeft, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import jsPDF from 'jspdf';

const AdminReports = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const loadReportData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/reports', {
        params: { start_date: dateRange.start, end_date: dateRange.end }
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Gagal memuat data laporan');
    } finally {
      setLoading(false);
    }
  }, [dateRange.end, dateRange.start]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    
    if (!token || !userStr) {
      navigate('/admin/login');
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.role !== 'admin') {
        navigate('/admin/login');
        return;
      }
    } catch (error) {
      navigate('/admin/login');
      return;
    }
    
    loadReportData();
  }, [loadReportData, navigate]);

  const exportToPDF = () => {
    if (!reportData) return;
    
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('ZERO FUTSAL', pageWidth / 2, 20, { align: 'center' });
    
    doc.setFontSize(14);
    doc.text('Laporan Pemasukan', pageWidth / 2, 30, { align: 'center' });
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Periode: ${dateRange.start} - ${dateRange.end}`, pageWidth / 2, 38, { align: 'center' });
    
    // Summary Section
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Ringkasan Pembayaran', 20, 55);
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    
    let y = 65;
    const lineHeight = 8;
    
    // Payment Summary Table Header
    doc.setFillColor(34, 197, 94);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('Metode Pembayaran', 25, y);
    doc.text('Jumlah Transaksi', 100, y);
    doc.text('Total', 150, y);
    
    doc.setTextColor(0, 0, 0);
    y += lineHeight + 2;
    
    // QRIS
    doc.text('QRIS', 25, y);
    doc.text(String(reportData.summary.qris.count), 100, y);
    doc.text(formatCurrency(reportData.summary.qris.total), 150, y);
    y += lineHeight;
    
    // Transfer
    doc.text('Transfer Bank', 25, y);
    doc.text(String(reportData.summary.transfer.count), 100, y);
    doc.text(formatCurrency(reportData.summary.transfer.total), 150, y);
    y += lineHeight;
    
    // Cash
    doc.text('Tunai (Cash)', 25, y);
    doc.text(String(reportData.summary.cash.count), 100, y);
    doc.text(formatCurrency(reportData.summary.cash.total), 150, y);
    y += lineHeight + 2;
    
    // Total line
    doc.setFont('helvetica', 'bold');
    doc.setFillColor(229, 231, 235);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.text('TOTAL', 25, y);
    doc.text(String(reportData.summary.total_transactions), 100, y);
    doc.text(formatCurrency(reportData.summary.total_revenue), 150, y);
    
    y += lineHeight + 15;
    
    // Transaction Details Header
    doc.setFontSize(12);
    doc.text('Detail Transaksi', 20, y);
    y += 10;
    
    // Table headers
    doc.setFontSize(8);
    doc.setFillColor(34, 197, 94);
    doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
    doc.setTextColor(255, 255, 255);
    doc.text('ID', 22, y);
    doc.text('Tanggal', 42, y);
    doc.text('Customer', 72, y);
    doc.text('Metode', 110, y);
    doc.text('Diskon', 132, y);
    doc.text('Sumber', 152, y);
    doc.text('Total', 172, y);
    
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
    y += lineHeight;
    
    // Transaction rows
    reportData.transactions.slice(0, 25).forEach((tx, index) => {
      if (y > 270) {
        doc.addPage();
        y = 20;
      }
      
      const bgColor = index % 2 === 0 ? [249, 250, 251] : [255, 255, 255];
      doc.setFillColor(...bgColor);
      doc.rect(20, y - 5, pageWidth - 40, lineHeight, 'F');
      
      doc.text(tx.booking_id.substring(0, 8), 22, y);
      doc.text(tx.booking_date, 42, y);
      doc.text(tx.customer_name.substring(0, 15), 72, y);
      doc.text(String(tx.payment_method || '-').substring(0, 10), 110, y);
      doc.text(formatCurrency(tx.discount_amount || 0), 132, y);
      doc.text(String(tx.discount_source || '-').substring(0, 8), 152, y);
      doc.text(formatCurrency(tx.total_payable ?? tx.total ?? 0), 172, y);
      
      y += lineHeight;
    });
    
    // Footer
    y += 10;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.text(`Digenerate pada: ${new Date().toLocaleString('id-ID')}`, 20, y);
    doc.text('Zero Futsal - Sistem Booking', pageWidth - 20, y, { align: 'right' });
    
    // Save PDF
    doc.save(`laporan_pemasukan_zero_futsal_${dateRange.start}_${dateRange.end}.pdf`);
    toast.success('Laporan berhasil di-export ke PDF');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-zinc-800 bg-background-paper/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <img src="/zero-logo.png" alt="Logo" className="w-10 h-10 object-contain rounded-md" />
              <span className="text-2xl font-heading font-black italic text-primary uppercase">Laporan</span>
            </div>
            <Link
              to="/admin/dashboard"
              className="text-zinc-400 hover:text-primary font-medium transition-colors flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex gap-4 mb-8">
            <Link
              to="/admin/reports/pemasukan"
              className={`px-6 py-3 font-bold uppercase tracking-wide transition-colors ${
                location.pathname.includes('/pemasukan')
                  ? 'bg-primary text-black'
                  : 'bg-zinc-900 text-zinc-300 hover:text-primary border border-zinc-800'
              }`}
            >
              Pemasukan
            </Link>
            <Link
              to="/admin/reports/pengeluaran"
              className={`px-6 py-3 font-bold uppercase tracking-wide transition-colors ${
                location.pathname.includes('/pengeluaran')
                  ? 'bg-primary text-black'
                  : 'bg-zinc-900 text-zinc-300 hover:text-primary border border-zinc-800'
              }`}
            >
              Pengeluaran
            </Link>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <h1 className="text-4xl font-heading font-bold italic uppercase text-white">Laporan Pemasukan</h1>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2">
                <Calendar className="w-4 h-4 text-zinc-400" />
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  className="bg-transparent text-white text-sm outline-none"
                  data-testid="report-start-date"
                />
                <span className="text-zinc-500">-</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  className="bg-transparent text-white text-sm outline-none"
                  data-testid="report-end-date"
                />
              </div>
              
              <button
                onClick={exportToPDF}
                disabled={loading || !reportData}
                data-testid="export-pdf-btn"
                className="bg-primary text-black hover:bg-primary-hover rounded-none skew-btn px-6 py-2 font-bold uppercase tracking-wider transition-all duration-200 shadow-[4px_4px_0px_0px_#FFFFFF] hover:shadow-[6px_6px_0px_0px_#FFFFFF] disabled:opacity-50 flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export PDF
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : reportData ? (
            <>
              {/* Payment Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
                <div className="bg-zinc-900 border-l-4 border-blue-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <QrCode className="w-8 h-8 text-blue-500" />
                    <span className="text-2xl font-black text-white">{reportData.summary.qris.count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">QRIS</div>
                  <div className="text-lg font-bold text-blue-400">{formatCurrency(reportData.summary.qris.total)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-purple-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <CreditCard className="w-8 h-8 text-purple-500" />
                    <span className="text-2xl font-black text-white">{reportData.summary.transfer.count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Transfer Bank</div>
                  <div className="text-lg font-bold text-purple-400">{formatCurrency(reportData.summary.transfer.total)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-green-500 rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <Banknote className="w-8 h-8 text-green-500" />
                    <span className="text-2xl font-black text-white">{reportData.summary.cash.count}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Tunai (Cash)</div>
                  <div className="text-lg font-bold text-green-400">{formatCurrency(reportData.summary.cash.total)}</div>
                </div>

                <div className="bg-zinc-900 border-l-4 border-primary rounded-r-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <FileText className="w-8 h-8 text-primary" />
                    <span className="text-2xl font-black text-white">{reportData.summary.total_transactions}</span>
                  </div>
                  <div className="text-sm text-zinc-400 font-semibold uppercase mb-1">Total Transaksi</div>
                  <div className="text-lg font-bold text-primary">{formatCurrency(reportData.summary.total_revenue)}</div>
                </div>
              </div>

              {/* Transactions Table */}
              <h2 className="text-2xl font-heading font-bold uppercase text-white mb-6">Detail Transaksi</h2>
              <div className="overflow-x-auto bg-zinc-900 border border-zinc-800 rounded-lg">
                <table className="w-full" data-testid="transactions-table">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">ID</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Tanggal</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Customer</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Lapangan</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Jam</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Metode</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Status</th>
                      <th className="text-left py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Sumber Diskon</th>
                      <th className="text-right py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Diskon</th>
                      <th className="text-right py-4 px-4 text-sm font-semibold text-zinc-400 uppercase">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.transactions.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-8 text-zinc-500">
                          Tidak ada transaksi dalam periode ini
                        </td>
                      </tr>
                    ) : (
                      reportData.transactions.map((tx) => (
                        <tr key={tx.booking_id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                          <td className="py-4 px-4 text-sm text-white font-mono">{tx.booking_id.substring(0, 8)}</td>
                          <td className="py-4 px-4 text-sm text-white">{tx.booking_date}</td>
                          <td className="py-4 px-4 text-sm text-white">{tx.customer_name}</td>
                          <td className="py-4 px-4 text-sm text-white">{tx.venue_name}</td>
                          <td className="py-4 px-4 text-sm text-white">{tx.time_slots?.join(', ') || '-'}</td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                              tx.payment_method === 'qris' ? 'bg-blue-500/20 text-blue-400' :
                              tx.payment_method === 'transfer' ? 'bg-purple-500/20 text-purple-400' :
                              tx.payment_method === 'cash' ? 'bg-green-500/20 text-green-400' :
                              'bg-zinc-700 text-zinc-400'
                            }`}>
                              {tx.payment_method || '-'}
                            </span>
                          </td>
                          <td className="py-4 px-4">
                            <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                              tx.payment_status === 'verified' ? 'bg-primary/20 text-primary' :
                              tx.payment_status === 'pending' ? 'bg-secondary/20 text-secondary' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {tx.payment_status === 'verified' ? 'Lunas' :
                               tx.payment_status === 'pending' ? 'Pending' : 'Cancelled'}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-white">{tx.discount_source || '-'}</td>
                          <td className="py-4 px-4 text-sm text-white text-right font-semibold">
                            {Number(tx.discount_amount || 0) > 0 ? formatCurrency(tx.discount_amount) : '-'}
                          </td>
                          <td className="py-4 px-4 text-sm text-white text-right font-semibold">
                            {formatCurrency(tx.total_payable ?? tx.total)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-zinc-500">
              Tidak ada data laporan
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AdminReports;
